import { Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import lottie, { type AnimationItem } from 'lottie-web'

export const SimpleAnimationCard = ({
  animationSource,
}: {
  animationSource: string | undefined | null
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!animationSource) return

    let animation: AnimationItem | null = null
    let cancelled = false

    const fetchAnimation = async () => {
      setLoading(true)

      try {
        const response = await fetch(animationSource)

        if (!response.ok) {
          console.error('Failed to load animation:', response.statusText)
          return
        }

        const animationData = await response.json()
        if (cancelled || !containerRef.current) return

        // Load the Lottie animation inside this specific container
        animation = lottie.loadAnimation({
          container: containerRef.current as Element,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData,
          rendererSettings: {
            // Scale to fill the 200x200 box (crop if aspect ratios differ)
            preserveAspectRatio: 'xMidYMid slice',
          },
        })

        // Ensure the internal SVG fills the container
        animation.addEventListener('DOMLoaded', () => {
          const svg = containerRef.current?.querySelector('svg')
          if (svg) {
            ;(svg as SVGElement).style.width = '100%'
            ;(svg as SVGElement).style.height = '100%'
            ;(svg as SVGElement).setAttribute(
              'preserveAspectRatio',
              'xMidYMid slice'
            )
          }
        })
      } catch (error) {
        console.error('Error fetching animation:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchAnimation()

    // Cleanup to prevent duplicate animations on remount (React Strict Mode)
    return () => {
      cancelled = true
      if (animation) {
        animation.destroy()
        animation = null
      }
    }
  }, [animationSource])

  if (animationSource === null || animationSource === undefined) {
    return <Typography>No animation</Typography>
  }

  return (
    <>
      {loading && <Typography>Loading animation...</Typography>}

      <div
        ref={containerRef}
        style={{
          transform: 'scale(1.5)',
          transformOrigin: 'center',
          width: 200,
          height: 200,
          overflow: 'hidden',
        }}
      />
    </>
  )
}
